import type { FC } from "react";
import "@web/src/styles/landing.scss";
import TodayIcon from "@web/public/to-day.svg";

interface PrivacyPageProps {}

const PrivacyPage: FC<PrivacyPageProps> = ({}) => {
  return (
    <main>
      <header className="flex flex-row gap-4 items-center">
        <TodayIcon className="w-7 h-7" />
        <h1>Privacy Policy</h1>
      </header>
      <div>
        <h2>1. Introduction</h2>
        <p>
          This Privacy Policy describes how To-day collects, uses, and discloses personal information when you use our
          website.
        </p>
        <p>
          This Privacy Policy applies to both the To-day website located at{" "}
          <a href="https://www.to-day.app">https://www.to-day.app</a> and the iOS/Android apps. By using To-day, you
          agree to the terms of this Privacy Policy.
        </p>
        <h3>1.1 Definition of terms</h3>
        <ul>
          <li>
            <strong>User Account Data</strong> - information relating to your account details like email, name, etc
          </li>
          <li>
            <strong>User Content Data</strong> - information relating to your content like notes, todos, etc
          </li>
          <li>
            <strong>Google Calendar Data</strong> - information retrieved from your Google Calendar account, including
            calendar events, event metadata (titles, dates, times, descriptions), and calendar identifiers
          </li>
        </ul>

        <h2>2. What do we collect?</h2>
        <div>
          <h3>2.1 User Account Data</h3>
          <ul>
            <li>
              When you login with Google, we store your name, email and google id as User Account Data. Your User
              Account Data is used for the purposes of the functionalities of the application, and is in no other way
              being used to identify you outside of the application.
            </li>
          </ul>
          <h3>2.2 User Content Data</h3>
          <ul>
            <li>
              We store your <strong>User Content Data</strong> in the form of notes, todos, etc. Your{" "}
              <strong>User Content Data</strong> is used for the purposes of the functionalities of the application, and
              is not shared with anyone. See <a href="#3">How is your information being used?</a> for more information.
            </li>
          </ul>
          <h3>2.3 Payment Data</h3>
          <ul>
            <li>
              When you make a purchase on the platform, we do not store any of the information, and the information is
              handled by the payment providers, which in our case is <a href="https://revenuecat.com/">RevenueCat</a>{" "}
              and the respective upstream payment providers like Google Play, Apple App Store, and Stripe.
            </li>
          </ul>
          <h3>2.4 Google Calendar Data</h3>
          <ul>
            <li>
              When you connect your Google Calendar, we access your calendar events using the Google Calendar API. This
              data is used solely to display your calendar events within To-day so you can manage your schedule alongside
              your tasks. We store calendar event data only as needed to provide this functionality.
            </li>
            <li>
              You can revoke To-day&apos;s access to your Google Calendar at any time through your{" "}
              <a href="https://myaccount.google.com/permissions">Google Account permissions</a>.
            </li>
          </ul>
          <h3>2.5 Analytics</h3>
          <ul>
            <li>
              When you use our services, we collect analytics on how you use our products and services. We use that
              information to continually improve the products and services we provide to you. This information is
              rudimentary, and involves how often you click this button, or perform that action.
            </li>
          </ul>
        </div>
        <h2 id="3">3. How is your information being used?</h2>
        <ul>
          <li>
            At no point in time will any of the information we collect be sold or shared with third parties, outside of
            the functionalities of the application.
          </li>
          <li>
            Your <strong>User Account Data</strong> may be shared with the above mentioned payment providers for the
            purposes of processing your payments and the entitlements you have purchased.
          </li>
          <li>
            Your <strong>User Content Data</strong> is not shared with anyone, and is only accessible by you. We do not
            look at your <strong>User Content Data</strong> unless requested by you (i.e. for the purposes of bug
            fixes).
          </li>
          <li>
            Your <strong>Google Calendar Data</strong> is used exclusively to display your calendar events within
            To-day. This data is not shared with any third parties, is not used for advertising or any purpose unrelated
            to the app&apos;s functionality, and is not used to train AI models.
          </li>
        </ul>

        <h2>4. Google API Services Usage Disclosure</h2>
        <p>
          To-day&apos;s use and transfer to any other app of information received from Google APIs will adhere to the{" "}
          <a href="https://developers.google.com/terms/api-services-user-data-policy">
            Google API Services User Data Policy
          </a>
          , including the{" "}
          <a href="https://developers.google.com/terms/api-services-user-data-policy#limited-use">
            Limited Use requirements
          </a>
          .
        </p>
        <p>Specifically, To-day:</p>
        <ul>
          <li>
            Only uses Google Calendar data to provide and improve user-facing features visible in the app (displaying
            your calendar events alongside your tasks).
          </li>
          <li>Does not transfer Google Calendar data to third parties unless necessary for security or legal purposes.</li>
          <li>
            Does not use Google Calendar data for serving advertisements, retargeting, or interest-based advertising.
          </li>
          <li>
            Does not allow humans to read your Google Calendar data unless you provide affirmative consent, it is
            necessary for security purposes (e.g. investigating a bug), or it is required to comply with applicable law.
          </li>
        </ul>
        <h2>5. How we protect your information</h2>
        <p>
          We take reasonable measures to protect your personal information, putting in place the necessary technical and
          organizational measures to ensure the security of your information. We use industry-standard encryption
          techniques to protect your information and ensure its confidentiality. That said, no method of transmission
          over the internet or electronic storage is 100% secure, and we cannot guarantee the absolute security of your
          information. We do not use any method of transmission that is not secure.
        </p>
        <h2>6. Changes to This Privacy Policy</h2>
        <p>
          We may update this Privacy Policy from time to time. We encourage you to review this Privacy Policy frequently
          to stay informed about how we are protecting your personal information.
        </p>
        <h2>7. Contact Us</h2>
        <p>
          If you have any questions about this Privacy Policy, please contact me at <a>moosedevapps@gmail.com</a> or dm
          me on Instagram <a href="https://www.instagram.com/moosedevapps/">@moosedevapps</a>.
          <br />
          <br />
          Last updated: April 2026 <br />
          Effective date: April 2026
        </p>
      </div>
    </main>
  );
};

export default PrivacyPage;
